import type {
  AgentArtifactCapabilityMode,
  AgentCapabilities,
  AgentCapabilityMode,
  AgentChatCapabilities,
  AgentMode,
} from '@tabbio/types';
import {
  persistPreference,
  readPersistedPreference,
} from './app-preferences-storage';

/**
 * The composer's Research and Image controls and its Result chip.
 *
 * Like the model pick (`agent-model-preference.ts`) this is a sticky *device*
 * preference rather than a per-thread one: it survives a reload on web
 * (`localStorage`) and a relaunch on the phone (SecureStore), and every mounted
 * composer reads the same snapshot.
 *
 * What the user picked is stored as picked. Availability (feature flags, the
 * canary allowlist, the plan) is a server answer, so it is applied on the way
 * out, in {@link resolveAgentCapabilitiesRequest}, never written back: a
 * capability that comes back after an upgrade returns to the user's own
 * choice instead of a value this device invented.
 */
export const AGENT_CAPABILITIES_STORAGE_KEY = 'tabbio-agent-capabilities';

export type AgentCapabilitiesPreference = {
  research: AgentCapabilityMode;
  image: AgentCapabilityMode;
  artifact: AgentArtifactCapabilityMode;
};

export type AgentCapabilityKey = keyof AgentCapabilitiesPreference;

export const AGENT_CAPABILITY_MODES: readonly AgentCapabilityMode[] = [
  'auto',
  'on',
  'off',
];

/**
 * The Result choices in menu order: Auto, Summary, Document, Slides, Page
 * (a web page), Image.
 */
export const AGENT_ARTIFACT_MODES: readonly AgentArtifactCapabilityMode[] = [
  'auto',
  'summary',
  'document',
  'slides',
  'page',
  'image',
];

/**
 * The Result choices a Task offers: the chat's list without Page. The Tasks
 * planner has no page report yet (compose_report makes a summary, a document
 * or slides), so a Page pick would silently run as Auto. Tasks pages are a
 * follow-up; until then every Tasks control resolves through
 * {@link resolveTaskCapabilityControls}, and chat keeps the full list.
 */
export const TASK_ARTIFACT_MODES: readonly AgentArtifactCapabilityMode[] =
  AGENT_ARTIFACT_MODES.filter((mode) => mode !== 'page');

export const DEFAULT_AGENT_CAPABILITIES_PREFERENCE: AgentCapabilitiesPreference =
  Object.freeze({ research: 'auto', image: 'auto', artifact: 'auto' });

function isCapabilityMode(value: unknown): value is AgentCapabilityMode {
  return (
    typeof value === 'string' &&
    (AGENT_CAPABILITY_MODES as readonly string[]).includes(value)
  );
}

function isArtifactMode(value: unknown): value is AgentArtifactCapabilityMode {
  return (
    typeof value === 'string' &&
    (AGENT_ARTIFACT_MODES as readonly string[]).includes(value)
  );
}

/**
 * Accepts the stored JSON string or an object. Anything unreadable falls back
 * to Auto field by field, because the server answers an invalid value with a
 * 400 and a corrupted preference must never wedge chat.
 */
export function normalizeAgentCapabilitiesPreference(
  raw: unknown,
): AgentCapabilitiesPreference {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      value = null;
    }
  }
  const record =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    research: isCapabilityMode(record.research) ? record.research : 'auto',
    image: isCapabilityMode(record.image) ? record.image : 'auto',
    artifact: isArtifactMode(record.artifact) ? record.artifact : 'auto',
  };
}

/**
 * One control changed. Two picks are coupled so the controls can never ask for
 * opposite things: choosing the Image result while the Image control is Off
 * brings Image back to Auto (the result is an explicit ask), and switching
 * Image Off drops an Image result back to Auto.
 */
export function applyAgentCapabilityChange<K extends AgentCapabilityKey>(
  current: AgentCapabilitiesPreference,
  key: K,
  value: AgentCapabilitiesPreference[K],
): AgentCapabilitiesPreference {
  const next = normalizeAgentCapabilitiesPreference({ ...current, [key]: value });
  if (key === 'artifact' && next.artifact === 'image' && next.image === 'off') {
    return { ...next, image: 'auto' };
  }
  if (key === 'image' && next.image === 'off' && next.artifact === 'image') {
    return { ...next, artifact: 'auto' };
  }
  return next;
}

// ── Sticky preference (external store) ─────────────────────────────────────

let currentSnapshot: AgentCapabilitiesPreference | null = null;

type Listener = (preference: AgentCapabilitiesPreference) => void;

const listeners = new Set<Listener>();

export function readAgentCapabilitiesPreference(): AgentCapabilitiesPreference {
  if (currentSnapshot !== null) return currentSnapshot;
  currentSnapshot = normalizeAgentCapabilitiesPreference(
    readPersistedPreference(AGENT_CAPABILITIES_STORAGE_KEY),
  );
  return currentSnapshot;
}

export function subscribeAgentCapabilitiesPreference(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setAgentCapabilitiesPreference(
  next: AgentCapabilitiesPreference,
) {
  const normalized = normalizeAgentCapabilitiesPreference(next);
  // SecureStore writes are asynchronous; the in-memory snapshot moves first so
  // the control updates in the same frame as the tap.
  currentSnapshot = normalized;
  persistPreference(AGENT_CAPABILITIES_STORAGE_KEY, JSON.stringify(normalized));
  for (const listener of listeners) listener(normalized);
}

export function setAgentCapability<K extends AgentCapabilityKey>(
  key: K,
  value: AgentCapabilitiesPreference[K],
) {
  setAgentCapabilitiesPreference(
    applyAgentCapabilityChange(readAgentCapabilitiesPreference(), key, value),
  );
}

// ── Known availability ─────────────────────────────────────────────────────

let knownCapabilities: AgentCapabilities | null = null;

/**
 * The last `GET /api/agent/capabilities` answer, kept outside React so the chat
 * runtime (memoized for the thread's lifetime) can read it at send time, the
 * same way it reads the preference.
 */
export function rememberAgentCapabilities(value: AgentCapabilities | null) {
  knownCapabilities = value;
}

export function readKnownAgentCapabilities(): AgentCapabilities | null {
  return knownCapabilities;
}

export function resetAgentCapabilitiesPreferenceCacheForTests() {
  currentSnapshot = null;
  knownCapabilities = null;
}

// ── Reconciliation ─────────────────────────────────────────────────────────

/**
 * What one message is sent with. An unavailable Research or Image control is
 * sent as `off`; without documents the Result chip is hidden, so it is sent as
 * `auto`; an Image result without images mirrors the server and asks for a
 * `summary`. Before availability is known the stored picks go out unchanged:
 * the server resolves availability again either way.
 */
export function resolveAgentCapabilitiesRequest(
  preference: AgentCapabilitiesPreference,
  capabilities: AgentCapabilities | null | undefined,
): Required<AgentChatCapabilities> {
  if (!capabilities) return { ...preference };
  const research = capabilities.research.available ? preference.research : 'off';
  const image = capabilities.image.available ? preference.image : 'off';
  let artifact = preference.artifact;
  if (!capabilities.artifacts.available) {
    artifact = 'auto';
  } else if (artifact === 'image' && image === 'off') {
    artifact = 'summary';
  }
  return { research, image, artifact };
}

/**
 * The capabilities one chat request carries, read at send time. Employer
 * threads ignore them server side, so they send nothing. The phone passes
 * `DEFAULT_AGENT_CAPABILITIES_PREFERENCE`: it has no sticky controls, only
 * per-message chips and mentions, so every other message runs on Auto.
 */
export function readAgentCapabilitiesForRequest(
  mode: AgentMode,
  preference: AgentCapabilitiesPreference = readAgentCapabilitiesPreference(),
): Required<AgentChatCapabilities> | undefined {
  if (mode !== 'seeker') return undefined;
  return resolveAgentCapabilitiesRequest(preference, readKnownAgentCapabilities());
}

export type AgentCapabilityControls = {
  research: { visible: boolean; mode: AgentCapabilityMode };
  image: {
    visible: boolean;
    /** Plan-locked: shown with a lock, and tapping it opens the upgrade modal. */
    locked: boolean;
    mode: AgentCapabilityMode;
  };
  artifact: {
    visible: boolean;
    mode: AgentArtifactCapabilityMode;
    options: AgentArtifactCapabilityMode[];
  };
  anyVisible: boolean;
};

/**
 * The composer's view of the controls. A capability the account cannot use is
 * hidden, except an image that only needs a higher plan: that one stays on
 * screen, locked, as the way to the upgrade. Nothing shows until availability
 * is known, so a control never appears and then vanishes. `resultModes` is
 * the Result list to offer (chat: every mode; Tasks: `TASK_ARTIFACT_MODES`);
 * a pick outside it shows as Auto.
 */
export function resolveAgentCapabilityControls(
  preference: AgentCapabilitiesPreference,
  capabilities: AgentCapabilities | null | undefined,
  resultModes: readonly AgentArtifactCapabilityMode[] = AGENT_ARTIFACT_MODES,
): AgentCapabilityControls {
  const request = resolveAgentCapabilitiesRequest(preference, capabilities);
  const researchVisible = capabilities?.research.available === true;
  const imageAvailable = capabilities?.image.available === true;
  const imageLocked =
    !imageAvailable && capabilities?.image.reason === 'upgrade_required';
  const artifactVisible = capabilities?.artifacts.available === true;
  // The Image result needs images; every other result needs documents only.
  const options = resultModes.filter(
    (mode) => mode !== 'image' || imageAvailable,
  );
  const artifactMode = options.includes(request.artifact) ? request.artifact : 'auto';

  return {
    research: { visible: researchVisible, mode: request.research },
    image: {
      visible: imageAvailable || imageLocked,
      locked: imageLocked,
      mode: request.image,
    },
    artifact: { visible: artifactVisible, mode: artifactMode, options },
    anyVisible: researchVisible || imageAvailable || imageLocked || artifactVisible,
  };
}

/**
 * The controls of a Task (the Tasks composer chips, both builders and the
 * native rows and sheet): the chat's controls without the Page result, which
 * the Tasks planner cannot make yet. Tasks pages are a follow-up.
 */
export function resolveTaskCapabilityControls(
  preference: AgentCapabilitiesPreference,
  capabilities: AgentCapabilities | null | undefined,
): AgentCapabilityControls {
  return resolveAgentCapabilityControls(preference, capabilities, TASK_ARTIFACT_MODES);
}
